/* ── F3 lit-html conversion conventions (established here, wave A; waves B
 *    and C follow verbatim — see the F3a report for the full rationale) ──
 *
 * 1. Import path: `import { html, render, nothing } from 'lit'` (verified
 *    at runtime — html/render/nothing/svg all resolve). package.json's own
 *    dependency is `lit`, not `lit-html` (the latter is only a transitive
 *    dep) — every converted view imports from `lit`, never `lit-html`
 *    directly, so the codebase has exactly one import path for the whole
 *    conversion.
 * 2. `el.innerHTML = \`...\`` → `render(html\`...\`, el)`. Each
 *    document.getElementById(...)-scoped sub-render the original did (a
 *    card, a tbody, a tally strip) keeps its OWN render() call into that
 *    element — same granularity as the original's separate innerHTML
 *    assignments, not one render() for the whole section.
 * 3. `${esc(x)}` → `${x}` — lit-html escapes every child-position binding
 *    by default. `esc()` stays in lib/format.ts until wave C removes the
 *    last string-template consumer; converted views stop importing it.
 * 4. `title="${esc(t)}"` → `title="${t}"` — lit attribute bindings escape
 *    too; quoted mixed static+dynamic attribute values are unchanged.
 * 5. Inline `onclick="fn('${id}')"` → `@click=${() => fn(id)}`. A
 *    window-shim entry in main.ts is deleted only once NO unconverted
 *    template still references that global as a literal onclick="" string
 *    (grepped per file; see the F3a report's checklist).
 * 6. `.map(x => \`...\`).join('')` → `.map(x => html\`...\`)` (lit renders
 *    an array of TemplateResults with no separator — same as join('')).
 *    Where the original joined with a non-empty separator (` · `, '<br>'),
 *    use lit's `join()` directive (`lit/directives/join.js`) instead of
 *    hand-rolling an interleave.
 * 7. `${cond ? \`<div>...\` : ''}` → `${cond ? html\`<div>...\` : nothing}`.
 *    Where BOTH branches are plain text (no markup on either side), the
 *    ternary is left as plain strings — there's no lit-templating concern
 *    and no observable difference from `nothing`.
 * 8. Class/style string interpolation (`class="chip ${cls}"`) stays as-is
 *    inside the template — no classMap/styleMap directive introduced.
 * 9. Post-render `el.querySelector(...).onclick = ...` wiring moves into
 *    the template as `@click` on the element it targets. A handler
 *    targeting an element OUTSIDE the section container stays minimal
 *    imperative wiring (documented at its call site — none needed in this
 *    wave; see the F3a report).
 * 10. String-returning HTML helpers used by converted views get
 *     TemplateResult twins in hub/src/lib/templates.ts (chipT, tipT —
 *     mirroring lib/format.ts's chip() / lib/glossary.ts's tip() 1:1, the
 *     string original cited above each twin). The string originals stay in
 *     lib/ for views wave C hasn't reached yet.
 * 11. `unsafeHTML` (lit/directives/unsafe-html.js) is for HTML this app
 *     itself builds from trusted, non-end-user input — the markdown/
 *     dossier pipeline (renderMarkdown's output) is the canonical case.
 *     Every call site carries a `// UNSAFE-HTML JUSTIFICATION:` comment
 *     (input source + why safe) and is listed in the F3a report.
 *     AMENDMENT (discovered converting views/entity.ts): WS.ICON.* values
 *     (state/workspace.ts) are raw SVG-markup string constants — a second,
 *     equally trusted input class (developer-authored, never derived from
 *     run/payload/user data) that the ORIGINAL innerHTML templates also
 *     inserted unescaped. `${WS.ICON.down}` as a plain lit child binding
 *     would escape the SVG into visible text and break the button, so
 *     views/entity.ts's three icon sites use unsafeHTML too, each with its
 *     own justification comment. Flagged prominently in the F3a report for
 *     explicit sign-off — wave B hit the identical pattern once more
 *     (views/actions.ts's CSV-export button).
 *     RESOLVED (F3c): every WS.ICON consumer (views/actions.ts,
 *     ws/board.ts, ws/taskDrawer.ts, ws/dialogs.ts, views/entity.ts) is
 *     now lit-converted, so state/workspace.ts's `ICON` dictionary itself
 *     converted to real `svg\`\`` TemplateResults (requirement 4) — every
 *     `${WS.ICON.x}` site (including entity.ts's three, unsafeHTML'd since
 *     F3a) is a plain lit child binding again. This amendment is retired;
 *     item 11's markdown/dossier-only rule is back to being the whole
 *     story for the rest of the codebase.
 * 12. Before converting a view: grep app.css/workspace.css for `:empty`
 *     selectors touching its DOM, and grep the view for
 *     childNodes/children counting logic — lit's comment-node binding
 *     markers can defeat both. Wave A: zero `:empty` selectors exist in
 *     either stylesheet and none of the six views count child nodes — no
 *     adaptation needed (see the F3a report).
 * 13. Every view keeps its exact exported signature (`renderX(state)`);
 *     views/registry.ts is untouched.
 */

/* Verbatim port of renderWiki from service/review/index.html (~2960-3023)
 * — §5 Wikipedia & community.
 *
 * PARAMETERIZED (rule a): reads the global STATE — takes `state:
 * AppPayloadState` as an explicit first parameter.
 *
 * DEVIATION (dead local): the original computed `const negWiki =
 * wikis.filter(...)` (~2966) but never read it anywhere afterward in the
 * function body — a pure, side-effect-free dead computation. Dropped here
 * under TS `noUnusedLocals`, same treatment as the dead `others`/`terms`
 * locals documented in ui/popover.ts and views/timeline.ts.
 *
 * F3: innerHTML → lit render(); `usedDomains` (originally scoped inside an
 * IIFE that built the tbody rows string in one expression) is hoisted to a
 * plain local — lit doesn't need the IIFE, `langRows` is just another
 * local built before the render() call. Row/card click wiring
 * (tr.click/.profcard → openDrawer) moves from the trailing
 * querySelectorAll into `@click` on each row/card (convention #9).
 */
import { html, nothing, render } from 'lit';
import { join } from 'lit/directives/join.js';
import { domainOf, faviconUrl } from '../lib/format.ts';
import { chipT } from '../lib/templates.ts';
import { openDrawer } from '../ui/drawer.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

export function renderWiki(state: AppPayloadState): void {
  const el = document.getElementById('wikiBody')!;
  const wikis = state.assets.filter((a) => a.platform === 'Wikipedia');
  const community = state.assets.filter((a) => a.assetType === 'forum' || /commons|wikimiles/i.test(a.platform || ''));
  if (!wikis.length && !community.length){ render(html`<div class="why">No Wikipedia or community surfaces mapped in this run.</div>`, el); return; }

  const serpWikiHit = state.serp?.results.find((r) => /wikipedia\.org/.test(r.url) && r.page === 1);
  // only surface editions with genuinely notable editorial signals — a bare
  // "protection: none" is normal, not "activity worth watching"
  const activeEditing = wikis.filter((a) => a.fields && Object.entries(a.fields).some(([k,v]) =>
    /talk|sensitive|dispute|revert|coi|edit.?war|controvers/i.test(k + ' ' + String(v))));

  const finding = wikis.length
    ? html`${state.brand} has Wikipedia coverage in <b>${wikis.length === 9 ? '43 languages (8 verified in depth)' : wikis.length + ' tracked language editions'}</b>${serpWikiHit ? html` — the ${/he\./.test(serpWikiHit.url) ? 'Hebrew' : 'English'} article ranks <b>#${serpWikiHit.rank} on the branded page one</b>${serpWikiHit.rating === 'negative' ? ' and is rated Undesirable' : ''}` : nothing}${activeEditing.length ? `, with active editing on reputationally sensitive sections` : ''}.`
    : 'No Wikipedia coverage found for this client.';

  const usedDomains = new Set<string>();
  const langRows = wikis.map((a) => {
    const d = a.url && domainOf(a.url);
    // attach the SERP rank chip only once per wikipedia domain (rollup rows share en.)
    const hit = d && !usedDomains.has(d) && state.serp?.results.find((r) => domainOf(r.url) === d);
    if (d) usedDomains.add(d);
    const isRollup = /other/i.test(a.id) || /other/i.test(a.title);
    const lang = isRollup ? 'other languages'
      : (a.title.match(/\(([^)]+)\)/) || [])[1] || (a.url && (a.url.match(/\/\/(\w+)\.wikipedia/)||[])[1]) || a.title;
    return html`<tr class="click" data-id="${a.id}" @click=${() => openDrawer(state, a.id)}>
      <td><span class="langname">${lang}</span>${isRollup ? nothing : html`<div style="font-size:10.5px;color:var(--muted);font-family:var(--mono)">${domainOf(a.url)||''}</div>`}</td>
      <td>${a.sentiment ? chipT(a.sentiment === 'negative' ? 'Undesirable' : a.sentiment === 'positive' ? 'Desirable' : a.sentiment, a.sentiment === 'negative' ? 'bad' : a.sentiment === 'positive' ? 'ok' : a.sentiment === 'mixed' ? 'warn' : '') : chipT('unrated','')}</td>
      <td>${hit ? chipT('#'+hit.rank+((hit.page ?? 1)>1?' (page 2)':'')+(hit.rating==='negative'?' · rated Undesirable':''), hit.rating==='negative'?'bad':'info') : html`<span style="color:var(--muted);font-size:12px">—</span>`}</td>
      <td style="font-size:12.5px;color:var(--ink-2);max-width:520px">${(a.notes||'').slice(0,220)}${(a.notes||'').length>220?'…':''}</td>
    </tr>`;
  });

  render(html`
    <div class="finding">${finding}</div>
    <div class="why"><b>Why this matters:</b> Wikipedia is usually the single most influential surface the client cannot edit — it anchors the Knowledge Panel, feeds every AI engine, and holds sticky page-1 positions in most languages. The firm treats it as its own workstream, always through Wikipedia's conflict-of-interest rules: transparent Talk-page requests, never direct editing.</div>

    <div class="card" style="margin-bottom:16px">
      <h3>Article state by language</h3>
      <div class="sub">the languages that matter for this client, with what our research found on each</div>
      <table class="tbl wikilang"><thead><tr><th>Edition</th><th title="research judgment of the article's overall tone">Article tone (research)</th><th title="where the article ranks on the captured branded search, with IMPACT's rating">Branded-search rank</th><th>What we found</th></tr></thead>
      <tbody>${langRows}</tbody></table>
    </div>

    ${activeEditing.length ? html`<div class="card" style="margin-bottom:16px">
      <h3>Editorial activity worth watching</h3>
      <div class="sub">talk-page and edit-history signals from the Wikimedia API</div>
      ${activeEditing.map((a) => html`<div class="conflict" style="border-left-color:var(--amber)">
        <p><b>${a.title}</b></p>
        <div style="font-size:12.5px;color:var(--ink-2);line-height:1.6">${join(Object.entries(a.fields || {}).filter(([k,v]) => /talk|edit|protect|sensitive|size|revert|dispute/i.test(k + ' ' + String(v))).map(([k,v]) => html`<b>${k}:</b> ${String(v)}`), html`<br>`)}</div>
      </div>`)}</div>` : nothing}

    ${community.length ? html`<div class="card" style="margin-bottom:16px">
      <h3>Community & enthusiast surfaces</h3>
      <div class="sub">forums and community platforms where the brand conversation happens without the brand</div>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:10px">${community.map((a) => html`
        <div class="profcard c-${a.category}" data-id="${a.id}" @click=${() => openDrawer(state, a.id)}>
          <div class="pt">${a.url ? html`<img src="${faviconUrl(a.url,64)}" alt="">` : nothing}
            <div style="min-width:0;flex:1"><div class="n">${a.title}</div><div class="u">${domainOf(a.url)||''}</div></div></div>
          <div class="pbadges">${chipT(a.sentiment || 'unrated', a.sentiment === 'negative' ? 'bad' : a.sentiment === 'positive' ? 'ok' : '')}${chipT(a.status,'')}</div>
        </div>`)}</div></div>` : nothing}

    <div class="coibox"><b>The compliant path:</b> Five Blocks never edits Wikipedia directly on a client's behalf. The workstream is: document factual errors with reliable third-party sources → file transparent conflict-of-interest edit requests on the article's Talk page → build the independent, citable coverage (earned media, reference-grade owned content) that editors need in order to update the article. Slow, but durable — and it protects the client from the reputational cost of covert editing.</div>`, el);
}
